import useVideoControl from '@/hooks/useVideoControl';
import React, { useState, useCallback } from 'react';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import Preloader from '../Preloader';
import { SlowNetworkOverlay } from './SlowNetworkOverlay';
import { VideoPlayerSlider } from './VideoPlayerSlider';
import { useMeasure } from '@uidotdev/usehooks';
import { useVideoDownloadTracking } from './useVideoDownloadTracking';
import { useSlowNetworkWarning } from './useSlowNetworkWarning';

// ── Types ────────────────────────────────────────────────────────────────

interface VideoPlayerProps {
  src?: string;
  onSetCurRef?: (ref: HTMLVideoElement | null) => void;
  autoPlay?: boolean;
  style?: React.CSSProperties;
  PlayIcon?: React.ReactNode;
  ReplayIcon?: React.ReactNode;
  width?: string;
  height?: string;
  type?: 'video/webm' | 'video/mp4';
  controls?: boolean;
  children?: React.ReactNode;
  /** Show a "slow connection" overlay if video hasn't loaded within 7 seconds. */
  showSlowNetworkWarning?: boolean;
}

// ── Component ────────────────────────────────────────────────────────────

const VideoPlayer = React.forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (
    {
      src,
      onSetCurRef,
      autoPlay,
      style,
      PlayIcon,
      ReplayIcon,
      width = '330px',
      height,
      controls,
      children,
      showSlowNetworkWarning: showSlowNetworkWarningProp = false,
    },
    ref
  ) => {
    const {
      isPlaying,
      play,
      setCurRef,
      isReady,
      curTime,
      duration,
      curRef,
    } = useVideoControl();
    const [wrapperRef, wrapperSize] = useMeasure();

    const [rotate, setRotate] = useState(false);
    const [aspectRatioCSS, setAspectRatioCSS] = useState<number[] | undefined>(
      [3, 4] // Default to 3:4 aspect ratio
    );

    // ── Sentry download tracking ──────────────────────────────────────
    useVideoDownloadTracking(curRef, src);

    // ── Slow network warning ──────────────────────────────────────────
    const { showWarning: showSlowNetwork, dismiss: dismissSlowNetwork } =
      useSlowNetworkWarning(isReady, src, showSlowNetworkWarningProp);

    const handleLoadedMetadata = useCallback(
      (e: React.SyntheticEvent<HTMLVideoElement>) => {
        const video = e.currentTarget;
        // Safari preload hack
        if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
          video.currentTime = 0.1;
        }
        // Decide if we need to rotate
        const shouldRotate = video.videoWidth > video.videoHeight;
        setRotate(shouldRotate);

        // Compute CSS aspect-ratio string:
        const ratio = shouldRotate
          ? [video.videoHeight, video.videoWidth]
          : [video.videoWidth, video.videoHeight];
        setAspectRatioCSS(ratio);

        Sentry.addBreadcrumb({
          category: 'video-player',
          message: 'Video metadata loaded',
          level: 'info',
          data: {
            src,
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
            duration: video.duration,
            rotated: shouldRotate,
          },
        });
      },
      [src]
    );

    // Dynamic styles — only values that depend on runtime data
    const wrapperDynamicStyle: React.CSSProperties = {
      width,
      ...(height
        ? { height }
        : aspectRatioCSS?.length
        ? {
            aspectRatio: aspectRatioCSS.join(' / '),
            paddingTop: Math.floor((aspectRatioCSS[1] / aspectRatioCSS[0]) * 100) + '%',
          }
        : {}),
      ...style,
    };

    const videoStyle: React.CSSProperties = {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transformOrigin: 'center center',
      objectFit: 'cover',
      maxWidth: rotate ? 'unset' : '100%',
      transform: `translate(-50%, -50%)${rotate ? ' rotate(90deg)' : ''}`,
      ...(rotate && {
        height: wrapperSize.width + 'px',
        width: wrapperSize.height + 'px',
      }),
    };

    return (
      <div
        className="relative overflow-hidden"
        style={wrapperDynamicStyle}
        ref={wrapperRef}
      >
        <video
          ref={(node) => {
            setCurRef(node);
            onSetCurRef?.(node);
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          src={src}
          preload="metadata"
          playsInline
          autoPlay={autoPlay}
          onClick={play}
          onLoadedMetadata={handleLoadedMetadata}
          style={videoStyle}
        >
          {children}
        </video>

        {!isReady && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Preloader />
          </div>
        )}

        {showSlowNetwork && (
          <SlowNetworkOverlay onDismiss={dismissSlowNetwork} />
        )}

        {controls && (
          <div className="absolute bottom-0 z-[2] w-full">
            <VideoPlayerSlider
              currentTime={curTime}
              duration={duration}
              onTimeChange={(time) => {
                if (curRef) curRef.currentTime = time;
              }}
            />
          </div>
        )}

        <div className="pointer-events-none absolute inset-0 z-[3] flex items-center justify-center">
          {!isPlaying && isReady && curTime < duration && (
            PlayIcon || (
              <PlayCircleOutlined
                className="animate-pulse-size text-[40px] text-white"
              />
            )
          )}
          {!isPlaying && isReady && curTime >= duration && (
            ReplayIcon || (
              <ReloadOutlined className="text-[40px] text-white" />
            )
          )}
        </div>
      </div>
    );
  }
);

export default VideoPlayer;
